import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';

import {
    AreaRule,
    DateRule,
    Fields,
    Group,
    IntersectsRule,
    MultiSelectRule,
    MultiStringRule,
    NumberRule,
    Rule,
    SelectRule,
    StringRule,
    WithinRule
} from '../../Components/Common/UI/Filter/Interfaces/Filter';

// Used when criteria goes from advanced mode to quick mode
// Remove any invalid or things with a certain tag
function CleanCriteria(criteria: Group, fields: Fields, tag: string): Group {
    function cleanBranch(branch: Rule, fields: Fields): Rule | null {
        if (fields == null) {
            return null;
        }
        const type = branch?.type;
        const field = branch?.field;
        const fieldConfig = fields[field];
        switch (type) {
            case 'group':
                return null;
            case 'group-rule':
                return null;
            case 'rule': {
                const field = branch.field;
                if (field == null) {
                    return null;
                }
                if (fieldConfig == null) {
                    return null;
                }
                if (tag !== undefined && !fieldConfig.tags?.includes(tag)) {
                    return null;
                }
                switch (fieldConfig.type) {
                    case 'area':
                        return cleanArea(branch as AreaRule);
                    case 'select':
                        return cleanSelect(branch as SelectRule);
                    case 'multiselect':
                        return cleanMultiSelect(branch as MultiSelectRule);
                    case 'within':
                    case 'notwithin':
                        return cleanWithin(branch as WithinRule);
                    case 'intersects':
                    case 'notintersects':
                        return cleanIntersects(branch as IntersectsRule);
                    // Layer is legacy but can sort of work
                    case 'layer':
                        return cleanLayer(branch);
                    case 'integer':
                        return cleanNumber(branch as NumberRule);
                    case 'date':
                        return cleanDate(branch as DateRule);
                    case 'string':
                        return cleanString(branch as StringRule);
                    case 'multistring':
                        return cleanMultiString(branch as MultiStringRule);
                    default:
                        return null;
                }
            }
            default:
                return null;
        }
    }

    function cleanNumber(branch: NumberRule): NumberRule | null {
        const properties = branch?.properties;
        const operator = properties?.operator;
        const value0 = properties?.value0;
        const value1 = properties?.value1;
        if (
            ((value0 == null || value0 === '') &&
                (value1 == null || value1 === '')) ||
            operator == null ||
            operator === ''
        ) {
            return null;
        }
        return branch;
    }

    function cleanArea(branch: AreaRule): AreaRule | null {
        const properties = branch?.properties;
        if (
            (properties?.unit == null || properties?.unit === '') &&
            cleanNumber(branch) == null
        ) {
            return null;
        }
        return branch;
    }

    function cleanSelect(branch: SelectRule): SelectRule | null {
        const properties = branch?.properties;
        const operator = properties?.operator;
        const value = properties?.value;
        if (value == null || value === '' || operator == null || operator === '') {
            return null;
        }
        return branch;
    }

    function cleanMultiSelect(branch: MultiSelectRule): MultiSelectRule | null {
        const properties = branch?.properties;
        const operator = properties?.operator;
        const values = properties?.values;
        if (
            values == null ||
            values.length === 0 ||
            operator == null ||
            operator === ''
        ) {
            return null;
        }
        return branch;
    }

    function cleanIntersects(branch: IntersectsRule): null {
        return null;
    }

    function cleanWithin(branch: WithinRule): null {
        return null;
    }

    function cleanLayer(branch: Rule): null {
        return null;
    }

    function cleanDate(branch: DateRule): DateRule | null {
        const properties = branch?.properties;
        const operator = properties?.operator;
        const value0 = properties?.value0;
        const value1 = properties?.value1;
        if (
            ((value0 == null || value0 === '') &&
                (value1 == null || value1 === '')) ||
            operator == null ||
            operator === ''
        ) {
            return null;
        }
        return branch;
    }

    function cleanString(branch: StringRule): StringRule | null {
        const properties = branch?.properties;
        const operator = properties?.operator;
        const value = properties?.value;
        if (value == null || value === '' || operator == null || operator === '') {
            return null;
        }
        return branch;
    }

    function cleanMultiString(branch: MultiStringRule): MultiStringRule | null {
        const properties = branch?.properties;
        const operator = properties?.operator;
        const values = properties?.values;
        if (
            values == null ||
            values.length === 0 ||
            operator == null ||
            operator === ''
        ) {
            return null;
        }
        return branch;
    }

    const children = criteria.children;
    if (children != null) {
        const newChildren = children
            .map(child => cleanBranch(child, fields))
            .filter(Boolean) as Rule[];
        if (isEqual(children, newChildren)) {
            return criteria;
        } else {
            const newCriteria = cloneDeep(criteria);
            newCriteria.children = newChildren;
            return newCriteria;
        }
    }
    return criteria;
}

export default CleanCriteria;
